import { computed, onMounted, reactive, Ref, ref, UnwrapNestedRefs } from 'vue-demi'
import type { DeepImmutable } from '@svoi-ui/shared/generics'

export interface InfiniteQueryPaginationData {
  currentPage: number
  lastPage: number
}

export type InfiniteQueryPagination<T> = { models: T[]; pagination: InfiniteQueryPaginationData }

export type InfiniteQueryParams = {
  page: number
  count: number
}

interface InfiniteQueryProps<M> {
  query: (params: InfiniteQueryParams | UnwrapNestedRefs<InfiniteQueryParams>) => Promise<InfiniteQueryPagination<M>>
  queryParams: InfiniteQueryParams
  getNextPageParam?: (params: InfiniteQueryParams) => number
  getPreviousPageParam?: (params: InfiniteQueryParams) => number
  immediate?: boolean
}

export const useInfiniteQuery = <Model>(props: InfiniteQueryProps<Model>) => {
  const { queryParams, query, getNextPageParam, getPreviousPageParam, immediate = false } = props

  const isFetching = ref<boolean>(false)
  const isFirstFetching = ref<boolean | null>(null)

  const isFetchingNextPage = ref<boolean>(false)
  const hasNextPage = ref<boolean>(false)

  const isFetchingPreviousPage = ref<boolean>(false)
  const hasPreviousPage = ref<boolean>(false)

  const data: Ref<Model[]> = ref([])
  const initParams: DeepImmutable<InfiniteQueryParams> = { ...queryParams }
  const params = reactive<InfiniteQueryParams>({ ...queryParams })

  const possibleFetchPages = (pagination: InfiniteQueryPaginationData) => {
    hasNextPage.value = pagination.currentPage < pagination.lastPage
    hasPreviousPage.value = pagination.currentPage > 1
  }

  const fetchNextPage = async () => {
    if (!hasNextPage.value) {
      return
    }

    isFetching.value = true
    isFetchingNextPage.value = true

    params.page = getNextPageParam?.(params) ?? params.page + 1

    const { models, pagination } = await query(params)

    isFetching.value = false
    isFetchingNextPage.value = false

    data.value = [...data.value, ...models]
    possibleFetchPages(pagination)
  }

  const fetchPreviousPage = async () => {
    if (!hasPreviousPage.value) {
      return
    }

    isFetching.value = true
    isFetchingPreviousPage.value = true

    params.page = getPreviousPageParam?.(params) ?? params.page - 1

    const { models, pagination } = await query(params)

    isFetching.value = false
    isFetchingPreviousPage.value = false

    data.value = [...models, ...(data.value as Model[])]
    possibleFetchPages(pagination)
  }

  const fetchPage = async () => {
    isFetching.value = true

    if (isFirstFetching.value === null) {
      isFirstFetching.value = true
    }

    params.page = initParams.page
    params.count = initParams.count

    const { models, pagination } = await query(params)

    isFetching.value = false
    isFirstFetching.value = false
    data.value = models
    possibleFetchPages(pagination)
  }

  onMounted(() => {
    if (immediate) {
      fetchPage()
    }
  })

  const normalizedFirstFetching = computed(() => isFirstFetching.value ?? false)

  return {
    data,
    fetchPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetching,
    isFirstFetching: normalizedFirstFetching,
    isFetchingNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    hasPreviousPage
  }
}
